import { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "../components/FormField";
import { Plus, Pencil, Trash2, ShieldCheck, Mail } from "lucide-react";

const ROLE_KEYS = ["super_admin", "amministratore", "property_manager", "housekeeping", "manutentore", "proprietario", "customer_care"];
const FULL = ["super_admin", "amministratore"];
const ROLE_STYLE = {
  super_admin: "bg-primary text-primary-foreground", amministratore: "bg-indigo-100 text-indigo-700",
  property_manager: "bg-blue-100 text-blue-700", housekeeping: "bg-cyan-100 text-cyan-700",
  manutentore: "bg-amber-100 text-amber-800", proprietario: "bg-emerald-100 text-emerald-700",
  customer_care: "bg-rose-100 text-rose-700",
};

const empty = { email: "", password: "", name: "", role: "customer_care", allowed_apartment_ids: [], owner_id: "", active: true };

export default function UsersAdmin() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [apts, setApts] = useState([]);
  const [owners, setOwners] = useState([]);
  const [rolesMatrix, setRolesMatrix] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/apartments").then((r) => setApts(r.data));
    api.get("/owners").then((r) => setOwners(r.data));
    api.get("/roles").then((r) => {
      const m = {}; r.data.roles.forEach((x) => (m[x.key] = x.sections)); setRolesMatrix(m);
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setOpen(true); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setOpen(true); };
  const roleLabel = (r) => t(`role_${r}`);
  const aptName = (id) => apts.find((a) => a.id === id)?.name || id;

  const toggleApt = (id) => set("allowed_apartment_ids", form.allowed_apartment_ids.includes(id)
    ? form.allowed_apartment_ids.filter((x) => x !== id) : [...form.allowed_apartment_ids, id]);

  const save = async () => {
    if (!form.email.trim()) { toast.error(t("email")); return; }
    if (!form.id && !form.password) { toast.error(t("password")); return; }
    try {
      const payload = { ...form };
      if (form.id && !payload.password) delete payload.password;
      if (form.id) await api.put(`/users/${form.id}`, payload);
      else await api.post("/users", payload);
      toast.success(t("saved")); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => {
    if (!window.confirm(t("confirm"))) return;
    try { await api.delete(`/users/${id}`); toast.success(t("deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const showAllowed = !FULL.includes(form.role) && form.role !== "proprietario";
  const showOwner = form.role === "proprietario";

  return (
    <div className="space-y-6 fade-in" data-testid="users-page">
      <div className="flex justify-end">
        <Button data-testid="add-user-btn" onClick={openNew} className="gap-2 bg-primary hover:bg-primary/90"><Plus size={18} /> {t("createUser")}</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {users.map((u) => (
          <Card key={u.id} data-testid={`user-card-${u.id}`} className="border-border p-5 transition-all hover:-translate-y-1 hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShieldCheck size={18} /></div>
                <div>
                  <h3 className="font-head font-semibold text-slate-900">{u.name || u.email}</h3>
                  <p className="flex items-center gap-1 text-xs text-slate-500"><Mail size={12} /> {u.email}</p>
                </div>
              </div>
              {!u.active && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{t("inactive")}</span>}
            </div>
            <div className="mt-3">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_STYLE[u.role] || "bg-slate-100 text-slate-600"}`} data-testid={`user-role-${u.id}`}>{roleLabel(u.role)}</span>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              {FULL.includes(u.role) ? t("allApartments")
                : u.role === "proprietario" ? `${t("owner")}: ${owners.find((o) => o.id === u.owner_id)?.name || "—"}`
                : (u.allowed_apartment_ids?.length ? u.allowed_apartment_ids.map(aptName).join(", ") : t("allApartments"))}
            </p>
            <div className="mt-4 flex gap-2">
              <Button data-testid={`edit-user-${u.id}`} variant="outline" size="sm" className="flex-1 gap-1" onClick={() => openEdit(u)}><Pencil size={14} /> {t("edit")}</Button>
              {u.id !== me?.id && <Button data-testid={`delete-user-${u.id}`} variant="outline" size="sm" className="text-rose-600" onClick={() => del(u.id)}><Trash2 size={14} /></Button>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{form.id ? t("edit") : t("createUser")}</DialogTitle><DialogDescription>{t("usersPermissions")}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("name")} className="col-span-2"><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label={t("email")}><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label={form.id ? t("newPassword") : t("password")}><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} /></Field>
            <Field label={t("role")} className="col-span-2">
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_KEYS.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {rolesMatrix[form.role] && (
              <div className="col-span-2 rounded-lg bg-secondary/50 p-3">
                <div className="mb-1.5 text-xs font-medium text-slate-600">{t("accessSections")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {rolesMatrix[form.role].map((s) => (
                    <span key={s} className="rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-600 border border-border">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {showOwner && (
              <Field label={t("linkedOwner")} className="col-span-2">
                <Select value={form.owner_id || "none"} onValueChange={(v) => set("owner_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="user-owner"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {showAllowed && (
              <div className="col-span-2">
                <div className="mb-1.5 text-xs text-slate-500">{t("allowedApartments")} <span className="text-slate-400">({t("allApartments").toLowerCase()} = 0)</span></div>
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3">
                  {apts.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`user-apt-${a.id}`}>
                      <Checkbox checked={form.allowed_apartment_ids.includes(a.id)} onCheckedChange={() => toggleApt(a.id)} />
                      {a.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="col-span-2 flex items-center justify-between rounded-lg bg-secondary/50 p-3">
              <span className="text-sm text-slate-700">{t("accountActive")}</span>
              <Switch data-testid="user-active" checked={form.active} onCheckedChange={(v) => set("active", v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-user-btn" onClick={save} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
